/**
 * Pattern annotation overlay — renders detected `Pattern`s as READ-ONLY chart
 * annotations (like earnings / previous-close markers), not user drawing tools:
 * no grab handles, not persisted to `SavedDrawing[]`. Patterns are derived data,
 * recomputed from bars (see `patternDetection.ts`), not user-authored geometry.
 *
 * Figure construction (`buildPatternFigures`) is pure and unit-tested;
 * `registerPatternOverlay` wires it into a klinecharts overlay template. Detection
 * runs ONCE on data change (in the chart panel) — never inside `createPointFigures`,
 * which fires every frame.
 *
 * Strength is encoded three ways so a glance ranks the levels: line ALPHA scales
 * with confidence, line WIDTH steps up at three or more touches, and a faint BAND
 * shades the zone's price spread (S/R is a region price reacts in, not a hairline).
 */

import { registerOverlay } from 'klinecharts'
import { type OverlayFigure } from './overlayUtils'
import type { Pattern } from './patternDetection'
import { NO_DEFAULT_FIGURES, type OverlayTemplateParam, registerOnce } from './registrationUtils'

export const PATTERN_OVERLAY_NAME = 'patternAnnotation'

// Concrete colors (overlays paint to canvas, so they can't read CSS vars).
const PATTERN_RGB: Record<string, [number, number, number]> = {
  support: [34, 197, 94], // green
  resistance: [239, 68, 68], // red
  trendline: [59, 130, 246], // blue
  default: [148, 163, 184], // slate
}

const rgba = ([r, g, b]: [number, number, number], a: number) => `rgba(${r}, ${g}, ${b}, ${a})`
const clamp01 = (v: number) => Math.max(0, Math.min(1, v))

export const PATTERN_COLORS: Record<string, string> = Object.fromEntries(
  Object.entries(PATTERN_RGB).map(([k, c]) => [k, rgba(c, 0.9)]),
)

/**
 * Line color for a pattern. With `confidence`, alpha runs 0.35 (weak) to 0.9
 * (strong) so the strongest levels read first; without it, the fixed 0.9.
 */
export function colorForPattern(type: string, confidence?: number): string {
  const rgb = PATTERN_RGB[type] ?? PATTERN_RGB.default
  if (confidence == null || !Number.isFinite(confidence)) return rgba(rgb, 0.9)
  return rgba(rgb, 0.35 + 0.55 * clamp01(confidence))
}

/** Fill color for the zone band: the same hue, faint, nudged by confidence. */
export function bandColorForPattern(type: string, confidence?: number): string {
  const rgb = PATTERN_RGB[type] ?? PATTERN_RGB.default
  const c = confidence == null || !Number.isFinite(confidence) ? 0.5 : clamp01(confidence)
  return rgba(rgb, 0.08 + 0.1 * c)
}

/** Line width by touch count: two touches is a hairline, three or more is a
 *  proper level. Steps rather than a ramp so widths stay crisp at 1px / 2px. */
export function lineWidthForPattern(p: Pick<Pattern, 'scoreComponents'>): number {
  return (p.scoreComponents.touchCount ?? 0) >= 3 ? 2 : 1
}

/** Label text for a pattern. S/R: `price · confidence%` off the level. A
 *  trendline: its projected value at the right edge (`pivotPrice`, the number
 *  a trader acts on) plus the touch count, not the left endpoint. */
export function patternLabel(p: Pattern): string {
  const pct = `${Math.round(p.confidence * 100)}%`
  if (p.type === 'trendline') {
    const price = p.pivotPrice
    const priceStr = price != null && Number.isFinite(price) ? price.toFixed(2) : ''
    const touches = p.scoreComponents.touchCount
    const touchStr = touches ? ` · ${touches} touches` : ''
    return `${priceStr}${touchStr} · ${pct}`
  }
  const price = p.lines[0]?.from[1]
  const priceStr = price != null && Number.isFinite(price) ? price.toFixed(2) : ''
  return `${priceStr} · ${pct}`
}

/** Project the segment `from → to` on to `x = paneWidth`. The horizontal-ray
 *  technique: the pane's own width, so nothing off-screen leaves artefacts. A
 *  segment already past the edge, or a vertical one, is returned unchanged. */
export function extrapolateRight(from: Point, to: Point, paneWidth: number): Point {
  if (!Number.isFinite(paneWidth) || to.x >= paneWidth || to.x === from.x) return to
  const slope = (to.y - from.y) / (to.x - from.x)
  return { x: paneWidth, y: to.y + slope * (paneWidth - to.x) }
}

interface Point {
  x: number
  y: number
}

export interface PatternFigureInput {
  /** Line segments in PIXEL space (already mapped from timestamp/price). */
  lines: Array<{ from: Point; to: Point }>
  color: string
  /** Stroke width in px. Default 1. */
  lineWidth?: number
  /** Zone band in PIXEL space, drawn under the first line across its x-extent.
   *  Skipped when either edge is non-finite. */
  band?: { top: number; bottom: number; color: string }
  label?: { x: number; y: number; text: string; align?: 'left' | 'right' }
}

/**
 * Pure figure builder: optional zone band, line segments, optional label. Bails
 * to `[]` if any line coordinate is non-finite (klinecharts would render garbage
 * otherwise).
 */
export function buildPatternFigures(input: PatternFigureInput): OverlayFigure[] {
  const { lines, color, lineWidth = 1, band, label } = input
  const figs: OverlayFigure[] = []

  for (const ln of lines) {
    if (![ln.from, ln.to].every((c) => Number.isFinite(c.x) && Number.isFinite(c.y))) return []
  }

  const first = lines[0]
  if (band && first && Number.isFinite(band.top) && Number.isFinite(band.bottom)) {
    const x = Math.min(first.from.x, first.to.x)
    const width = Math.abs(first.to.x - first.from.x)
    const y = Math.min(band.top, band.bottom)
    const height = Math.abs(band.bottom - band.top)
    if (width > 0 && height > 0) {
      figs.push({
        type: 'rect',
        ignoreEvent: true,
        attrs: { x, y, width, height },
        styles: { style: 'fill', color: band.color },
      })
    }
  }

  for (const ln of lines) {
    figs.push({
      type: 'line',
      ignoreEvent: true,
      attrs: { coordinates: [ln.from, ln.to] },
      styles: { color, size: lineWidth },
    })
  }

  if (label && Number.isFinite(label.x) && Number.isFinite(label.y)) {
    figs.push({
      type: 'text',
      ignoreEvent: true,
      attrs: {
        x: label.x,
        y: label.y,
        text: label.text,
        baseline: 'bottom',
        align: label.align ?? 'left',
      },
      styles: { style: 'none', color },
    })
  }

  return figs
}

/** What `ChartManager.setPatternOverlays` stuffs into each overlay's extendData. */
export interface PatternExtendData {
  color?: string
  label?: string
  lineWidth?: number
  /** Half-height of the zone band in PRICE units (mapped to px per frame). */
  bandHalf?: number
  bandColor?: string
  /** Project the segment on to the right edge of the pane (trendlines). */
  extendRight?: boolean
  /** Hang the label off the segment's start (S/R, default) or its end (a
   *  trendline, where the value that matters is the one at current price). */
  labelAlign?: 'start' | 'end'
}

/** Register the `patternAnnotation` overlay template with klinecharts (once). */
export const registerPatternOverlay = registerOnce(() => {
  registerOverlay({
    name: PATTERN_OVERLAY_NAME,
    totalStep: 3, // two points (line from→to) + 1
    lock: true,
    ...NO_DEFAULT_FIGURES,
    createPointFigures: ({
      coordinates,
      overlay,
      yAxis,
      bounding,
    }: {
      coordinates: Array<{ x: number; y: number }>
      overlay?: { extendData?: unknown; points?: Array<{ value?: number }> }
      yAxis?: { convertToPixel?: (v: number) => number }
      bounding?: { width?: number }
    }) => {
      if (coordinates.length < 2) return []
      const ext = (overlay?.extendData ?? {}) as PatternExtendData
      const [from, rawTo] = coordinates
      const to =
        ext.extendRight && typeof bounding?.width === 'number'
          ? extrapolateRight(from, rawTo, bounding.width)
          : rawTo
      const price = overlay?.points?.[0]?.value
      let band: PatternFigureInput['band']
      if (
        ext.bandHalf &&
        ext.bandHalf > 0 &&
        typeof price === 'number' &&
        typeof yAxis?.convertToPixel === 'function'
      ) {
        band = {
          top: yAxis.convertToPixel(price + ext.bandHalf),
          bottom: yAxis.convertToPixel(price - ext.bandHalf),
          color: ext.bandColor ?? bandColorForPattern('default'),
        }
      }
      return buildPatternFigures({
        lines: [{ from, to }],
        color: ext.color ?? PATTERN_COLORS.default,
        lineWidth: ext.lineWidth,
        band,
        label: ext.label
          ? ext.labelAlign === 'end'
            ? { x: rawTo.x - 4, y: rawTo.y - 2, text: ext.label, align: 'right' }
            : { x: from.x + 4, y: from.y - 2, text: ext.label }
          : undefined,
      })
    },
  } as OverlayTemplateParam)
})
